import React, { useState, useEffect } from 'react';
import { Download, Filter, CreditCard, Building2, ArrowUpDown, ArrowUp, ArrowDown, ShieldCheck } from 'lucide-react';
import { Button, Input } from '../ui';
import SearchableSelect from '../ui/SearchableSelect';
import api from '../../services/api';
import * as XLSX from 'xlsx';
import PaymentDetailsModal from './PaymentDetailsModal';

const PaymentReceivedReportPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<any[]>([]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    travelDateFrom: '',
    travelDateTo: '',
    vendorId: '',
    paymentMethod: 'all',
    currency: 'all',
    itineraryId: ''
  });
  const [vendors, setVendors] = useState<any[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
  const [summary, setSummary] = useState({
    totalAmountIDR: 0,
    totalPayments: 0,
    byCurrency: {} as { [key: string]: number }
  });
  const [selectedItineraryId, setSelectedItineraryId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [verifyingId, setVerifyingId] = useState<number | null>(null);

  useEffect(() => {
    loadVendors();
    loadPaymentMethods();
    loadPayments();
  }, []);

  const loadVendors = async () => {
    try {
      const response = await api.get('/reports/vendors');
      if (response.data.success) {
        setVendors(response.data.data);
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const loadPaymentMethods = async () => {
    try {
      const response = await api.get('/reports/payment-methods');
      if (response.data.success) {
        setPaymentMethods(response.data.data);
      }
    } catch (error) {
      console.error('Error loading payment methods:', error);
    }
  };

  const loadPayments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);
      if (filters.travelDateFrom) params.append('travel_date_from', filters.travelDateFrom);
      if (filters.travelDateTo) params.append('travel_date_to', filters.travelDateTo);
      if (filters.vendorId) params.append('vendor_id', filters.vendorId);
      if (filters.paymentMethod !== 'all') params.append('payment_method', filters.paymentMethod);
      if (filters.currency !== 'all') params.append('currency', filters.currency);
      if (filters.itineraryId) params.append('itinerary_id', filters.itineraryId);

      const response = await api.get(`/reports/payments-received?${params.toString()}`);
      if (response.data.success) {
        setPayments(response.data.data.payments);
        setSummary(response.data.data.summary);
      }
    } catch (error) {
      console.error('Error loading payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedPayments = () => {
    if (!sortConfig) return payments;

    const sorted = [...payments].sort((a, b) => {
      let aVal = a[sortConfig.key];
      let bVal = b[sortConfig.key];

      // Handle date sorting
      if (sortConfig.key === 'payment_date') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }

      // Handle numeric sorting
      if (sortConfig.key === 'amount_paid' || sortConfig.key === 'idr_equivalent') {
        aVal = Number(aVal);
        bVal = Number(bVal);
      }

      // Handle string sorting
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const getSortIcon = (columnKey: string) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return <ArrowUpDown className="w-4 h-4" />;
    }
    return sortConfig.direction === 'asc' ? 
      <ArrowUp className="w-4 h-4" /> : 
      <ArrowDown className="w-4 h-4" />;
  };

  const handleRowClick = async (payment: any) => {
    setSelectedItineraryId(payment.itinerary_id);
    setShowModal(true);
  };

  const handleVerify = async (e: React.MouseEvent, payment: any) => {
    e.stopPropagation();
    const newStatus = !payment.is_verified;
    const action = newStatus ? 'verify' : 'unverify';
    if (!window.confirm(`Are you sure you want to ${action} this payment of ${payment.currency} ${payment.amount_paid.toLocaleString()} for ${payment.guest_name}?`)) return;
    setVerifyingId(payment.payment_id);
    try {
      const response = await api.post(`/reports/verify-payment/${payment.payment_id}`, { is_verified: newStatus });
      if (response.data.success) {
        setPayments(prev => prev.map(p => 
          p.payment_id === payment.payment_id ? { ...p, is_verified: newStatus } : p
        ));
      }
    } catch (error) {
      console.error('Error toggling verification:', error);
    } finally {
      setVerifyingId(null);
    }
  };

  const exportToExcel = () => {
    try {
      const exportData = getSortedPayments().map(payment => ({
        'Date': formatDate(payment.payment_date),
        'Itinerary': payment.itinerary_id,
        'Guest': payment.guest_name,
        'Vendor': payment.vendor_name,
        'Currency': payment.currency,
        'Amount': payment.amount_paid,
        'Payment Method': payment.payment_method || 'N/A',
        'IDR Equivalent': payment.idr_equivalent
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Payments Received');
      
      // Add summary sheet
      const summaryData = [
        { Metric: 'Total Amount (IDR)', Value: summary.totalAmountIDR },
        { Metric: 'Total Payments', Value: summary.totalPayments },
        { Metric: 'Average Payment (IDR)', Value: summary.totalPayments > 0 ? summary.totalAmountIDR / summary.totalPayments : 0 },
        { Metric: 'Date Range', Value: `${filters.startDate} to ${filters.endDate}` }
      ];
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Summary');
      
      XLSX.writeFile(wb, `payments_received_report_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report. Please try again.');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">💰 Payment Received Report</h1>
        <p className="text-gray-600">Generate and export payment received reports with filters</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CreditCard className="w-8 h-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Amount (IDR)</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(summary.totalAmountIDR)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 font-bold">#</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Payments</p>
              <p className="text-2xl font-bold text-gray-900">{summary.totalPayments}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 font-bold">Avg</span>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Average Payment</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(summary.totalPayments > 0 ? summary.totalAmountIDR / summary.totalPayments : 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date From</label>
            <Input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date To</label>
            <Input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date From</label>
            <Input
              type="date"
              value={filters.travelDateFrom || ''}
              onChange={(e) => handleFilterChange('travelDateFrom', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Travel Date To</label>
            <Input
              type="date"
              value={filters.travelDateTo || ''}
              onChange={(e) => handleFilterChange('travelDateTo', e.target.value)}
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
            <SearchableSelect
              options={vendors.map(vendor => ({ value: vendor === 'All Vendors' ? '' : vendor, label: vendor }))}
              value={filters.vendorId}
              onChange={(value) => handleFilterChange('vendorId', value as string)}
              placeholder="All Vendors"
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
            <SearchableSelect
              options={paymentMethods.filter(method => method !== 'All Methods').map(method => ({ value: method, label: method }))}
              value={filters.paymentMethod === 'all' ? '' : filters.paymentMethod}
              onChange={(value) => handleFilterChange('paymentMethod', value ? value as string : 'all')}
              placeholder="All Methods"
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
            <SearchableSelect
              options={[
                { value: 'USD', label: 'USD' },
                { value: 'IDR', label: 'IDR' },
                { value: 'INR', label: 'INR' }
              ]}
              value={filters.currency === 'all' ? '' : filters.currency}
              onChange={(value) => handleFilterChange('currency', value ? value as string : 'all')}
              placeholder="All Currencies"
              className="w-full"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Itinerary ID</label>
            <Input
              type="text"
              value={filters.itineraryId}
              onChange={(e) => handleFilterChange('itineraryId', e.target.value)}
              placeholder="WH_1234"
              className="w-full"
            />
          </div>
        </div>
        
        <div className="flex gap-3">
          <Button onClick={loadPayments} disabled={loading}>
            {loading ? 'Loading...' : 'Apply Filters'}
          </Button>
          <Button onClick={exportToExcel} variant="secondary" className="flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Results */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Payment Results ({payments.length})</h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead className="bg-gray-50">
              <tr>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('payment_date')}
                >
                  <div className="flex items-center gap-1">
                    Date
                    {getSortIcon('payment_date')}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('itinerary_id')}
                >
                  <div className="flex items-center gap-1">
                    Itinerary
                    {getSortIcon('itinerary_id')}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('guest_name')}
                >
                  <div className="flex items-center gap-1">
                    Guest
                    {getSortIcon('guest_name')}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('vendor_name')}
                >
                  <div className="flex items-center gap-1">
                    Agency
                    {getSortIcon('vendor_name')}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('amount_paid')}
                >
                  <div className="flex items-center gap-1">
                    Amount
                    {getSortIcon('amount_paid')}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('payment_method')}
                >
                  <div className="flex items-center gap-1">
                    Method
                    {getSortIcon('payment_method')}
                  </div>
                </th>
                <th 
                  className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSort('idr_equivalent')}
                >
                  <div className="flex items-center gap-1">
                    IDR
                    {getSortIcon('idr_equivalent')}
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getSortedPayments().map((payment, index) => (
                <tr key={payment.payment_id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRowClick(payment)}>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(payment.payment_date)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {payment.quotation_number ? (
                      <div>
                        <div className="font-mono font-bold text-blue-700">{payment.quotation_number}/{payment.version_number}</div>
                        <div className="text-[10px] text-gray-400">{payment.itinerary_id}</div>
                      </div>
                    ) : payment.itinerary_id}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 max-w-48 truncate" title={payment.guest_name}>
                    {payment.guest_name}
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 max-w-40 truncate" title={payment.vendor_name?.trim()}>
                    {payment.vendor_name?.trim()}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {payment.currency} {payment.amount_paid.toLocaleString()}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                      {payment.payment_method || 'N/A'}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(payment.idr_equivalent)}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={(e) => handleVerify(e, payment)}
                      disabled={verifyingId === payment.payment_id}
                      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full transition-colors ${
                        payment.is_verified
                          ? 'text-emerald-700 bg-emerald-100 hover:bg-emerald-200'
                          : 'text-gray-500 bg-gray-100 hover:bg-gray-200'
                      }`}
                    >
                      <ShieldCheck className="w-3 h-3" />
                      {verifyingId === payment.payment_id ? '...' : payment.is_verified ? 'VERIFIED' : 'VERIFY'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {payments.length === 0 && !loading && (
            <div className="text-center py-12">
              <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No payments found for the selected criteria</p>
            </div>
          )}
        </div>
      </div>

      {/* Payment Details Modal */}
      {showModal && selectedItineraryId && (
        <PaymentDetailsModal
          itineraryId={selectedItineraryId}
          onClose={() => {
            setShowModal(false);
            setSelectedItineraryId(null);
          }}
        />
      )}
    </div>
  );
};

export default PaymentReceivedReportPage;